/**
 * components/video/TestimonialPlayerStyles.tsx — website copy (2026-09-25) of
 * pf-platform components/testimonials/TestimonialPlayerStyles.tsx; see the
 * alias block at the top of .tplayer for the only change.
 *
 * TestimonialPlayerStyles — CSS for <TestimonialPlayer> (Round 4, 2026-09-24).
 *
 * Copied from the website's `.pm12-player` block (pianofantasy-web
 * app/pro-member/_v12/pm12-carousel.css "§3 ROUND 13"), which mirrors this
 * repo's lesson-player `lps-*` rules. Every `tplayer-*` rule here is the
 * `lps-*` rule of the same suffix. Scoped under `.tplayer`, so nothing outside
 * the player can match it; the locked lesson-player CSS is not touched.
 *
 * Token-only (kit-audit): the carousel's literal rgba/px values are written as
 * the platform's own player tokens (--ext-player-*, §13 Lesson Player Surface)
 * or as color-mix() of them at the carousel's exact alphas. The player is a
 * fixed-dark surface in both themes, so it uses the fixed-dark tokens
 * (--gold-on-dark, --text-muted-on-dark, --ext-surface-2-dark), never the
 * theme-swapping --gold / --text / --bg-3. One departure: the media backdrop is
 * --ext-player-bg (the kit's player dark) instead of pure black — there is no
 * black token and kit-audit refuses the literal. The 4:3 standard (Lars
 * 2026-09-25): the box is always a 4:3 frame and the video is shown whole
 * (contain) — a 4:3 video fills it, any other shape sits on a blurred copy of
 * itself (.tplayer__blur, the Wall of Love's values). Fullscreen keeps contain.
 */
export function TestimonialPlayerStyles() {
  return (
    <style>{`
      .tplayer {
        /* ── Website token aliases (2026-09-25 copy) ─────────────────────────
           The platform original reads pf-platform tokens that do not exist on
           pianofantasy.com. Each is re-declared HERE, scoped to .tplayer only,
           as the website token or the exact value the website's own player
           block already uses (.pm12-player in app/pro-member/_v12/
           pm12-carousel.css) — so both repos' players render the same pixels.
           Nothing outside the player can read these. */
        --gold-on-dark: var(--gold);                                  /* #D4B86E */
        --ext-player-bg: var(--bg);                                   /* #0E0A18 */
        --ext-player-control-icon: var(--text);                       /* #F4F0EA */
        --ext-player-control-icon-muted: rgba(244, 240, 234, 0.55);
        --ext-player-control-icon-time-mobile: rgba(244, 240, 234, 0.9);
        --ext-player-scrubber-track: rgba(244, 240, 234, 0.20);
        --ext-player-scrubber-fill: var(--cta);                       /* #562096 */
        --ext-player-scrubber-thumb: #FFFFFF;
        --ext-player-chrome-border: rgba(244, 240, 234, 0.10);
        --ext-surface-2-dark: #1C1629;
        --text-muted-on-dark: #BBA9CC;
        --cta-foreground: #FFFFFF;
        --fs-floor: 16px;
        --fs-body-secondary: 17px;
        --space-1: 4px;
        --space-2: 8px;
        --space-3: 12px;
        --space-4: 16px;
        --space-5: 20px;
        --space-6: 24px;
        --tplayer-active: var(--gold-on-dark);
        --tplayer-active-bg: color-mix(in srgb, var(--gold-on-dark) 14%, transparent);
        --tplayer-active-bg-hover: color-mix(in srgb, var(--gold-on-dark) 22%, transparent);
        --tplayer-btn-hover: color-mix(in srgb, var(--ext-player-control-icon) 10%, transparent);
        --tplayer-bar-0: color-mix(in srgb, var(--ext-player-bg) 92%, transparent);
        --tplayer-bar-40: color-mix(in srgb, var(--ext-player-bg) 72%, transparent);
        --tplayer-bar-75: color-mix(in srgb, var(--ext-player-bg) 32%, transparent);
        --tplayer-bar-100: color-mix(in srgb, var(--ext-player-bg) 12%, transparent);
        --tplayer-caption-bg: color-mix(in srgb, var(--ext-player-bg) 78%, transparent);
        --tplayer-menu-hover: color-mix(in srgb, var(--gold-on-dark) 6%, transparent);
        --tplayer-menu-border-subtle: color-mix(in srgb, var(--ext-player-control-icon) 6%, transparent);
        /* The buffering mark (.lps-buffering-overlay .lv-equaliser, reused from
           globals.css) paints with --gold / --gold-dim; on this fixed-dark
           surface they are the dark-mode golds in both themes.
           Website copy: the platform's "--gold: var(--gold-on-dark)" line is
           dropped — here --gold-on-dark is aliased FROM --gold above (the
           website has one dark theme), and keeping both would be a cycle. */
        position: relative;
        width: 100%;
        margin-inline: auto;
        border-radius: var(--radius-md);
        overflow: hidden;
        background: var(--ext-player-bg);
        container-type: inline-size;
        container-name: tplayer;
      }
      .tplayer__frame {
        position: absolute;
        inset: 0;
        overflow: hidden;
        background: var(--ext-player-bg);
        font-family: var(--font-sans);
        -webkit-tap-highlight-color: transparent;
      }
      .tplayer__frame:fullscreen { background: var(--ext-player-bg); }
      .tplayer__frame:-webkit-full-screen { background: var(--ext-player-bg); }
      .tplayer__frame[data-autohide="true"],
      .tplayer__frame[data-autohide="true"] * { cursor: none !important; }
      .tplayer__video {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        display: block;
        /* The 4:3 standard: whole, never cropped; the bands show the blurred copy. */
        object-fit: contain;
        background: transparent;
      }
      /* The 4:3 standard: the blurred copy behind a video that is not 4:3 —
         the Wall of Love's values exactly (wol.css .wl-video__blur). */
      .tplayer__blur {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
        filter: blur(20px);
        transform: scale(1.15);
        opacity: 0.6;
        pointer-events: none;
      }
      .tplayer__frame:fullscreen .tplayer__video,
      .tplayer__frame:-webkit-full-screen .tplayer__video { object-fit: contain; }
      .tplayer__tap {
        position: absolute;
        inset: 0;
        z-index: 5;
        cursor: pointer;
        background: transparent;
      }
      /* Before the first play: one big play button in the middle (the carousel
         card's own play affordance, which lives on the card there). */
      .tplayer__big-play {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 26;
        width: 72px;
        height: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        border-radius: 50%;
        background: var(--cta);
        color: var(--cta-foreground);
        /* Round 6 (Aria "play disc over real posters"): a 2px white ring, a 4px
           ink ring and a soft drop shadow, so the disc's edge reads on any
           poster frame. Same in both themes and on Pianoles. Rendered: white at
           90%, the player ink at 45%, black at 35% (Aria's values). The kit
           has no black token, so the shadow's black is derived from the ink
           token with relative-colour syntax. */
        box-shadow:
          0 0 0 2px color-mix(in srgb, var(--ext-player-scrubber-thumb) 90%, transparent),
          0 0 0 4px color-mix(in srgb, var(--ext-player-bg) 45%, transparent),
          0 4px 14px rgb(from var(--ext-player-bg) 0 0 0 / 0.35);
        cursor: pointer;
        padding: 0;
      }
      .tplayer__big-play:hover { background: var(--cta-hover); }
      /* Kit play button (.pf-play-btn, globals.css): stacking only — above the tap layer. */
      .tplayer__frame .tplayer__kit-play { z-index: 26; }
      .tplayer__big-play:focus-visible { outline: 2px solid var(--gold-on-dark); outline-offset: 3px; }
      .tplayer__frame:not([data-started="true"]) .tplayer-control-bar { opacity: 0; pointer-events: none; }
      .tplayer__frame:not([data-started="true"]) .tplayer-control-bar * { pointer-events: none; }
      .tplayer__error {
        position: absolute;
        inset: auto 0 0 0;
        z-index: 26;
        margin: 0;
        padding: var(--space-3) var(--space-4);
        background: var(--tplayer-bar-0);
        color: var(--ext-player-control-icon);
        font-size: var(--fs-floor);
      }

      /* .lps-caption-overlay */
      .tplayer-caption-overlay {
        position: absolute;
        bottom: var(--space-2);
        left: 50%;
        transform: translateX(-50%);
        z-index: 27;
        width: max-content;
        max-width: 88%;
        text-align: center;
        color: var(--cta-foreground);
        font-size: var(--fs-body-secondary);
        font-weight: 500;
        line-height: 1.4;
        pointer-events: none;
        white-space: pre-line;
        transition: bottom 1s ease-in-out;
      }
      .tplayer-caption-overlay__text {
        display: inline;
        background: var(--tplayer-caption-bg);
        padding: 0 var(--space-2) var(--space-1);
        border-radius: var(--radius-sm);
        -webkit-box-decoration-break: clone;
        box-decoration-break: clone;
      }
      .tplayer__frame[data-controls-shown="true"] .tplayer-caption-overlay {
        bottom: var(--tplayer-cap-lift, 92px);
        transition: bottom 0.15s ease;
      }

      /* .lps-control-bar */
      .tplayer-control-bar {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 28;
        width: 100%;
        padding: calc(var(--space-6) + var(--space-1)) var(--space-5) 0;
        display: flex;
        flex-direction: column;
        background: linear-gradient(
          to top,
          var(--tplayer-bar-0) 0%,
          var(--tplayer-bar-40) 40%,
          var(--tplayer-bar-75) 75%,
          var(--tplayer-bar-100) 100%
        );
        transform: translateY(0);
        transition: opacity 0.15s ease, transform 0.15s ease;
        pointer-events: none;
        box-sizing: border-box;
      }
      .tplayer-control-bar > * { pointer-events: auto; }
      .tplayer-control-bar[data-autohide="true"] {
        opacity: 0;
        transform: translateY(16px);
        transition: opacity 1s ease-in-out, transform 1s ease-in-out;
      }
      .tplayer-control-bar[data-autohide="true"],
      .tplayer-control-bar[data-autohide="true"] * { pointer-events: none; }

      /* .lps-scrubber-row / .lps-scrubber */
      .tplayer-scrubber-row {
        padding: var(--space-3) 0 var(--space-1);
        touch-action: pan-y;
      }
      .tplayer-scrubber {
        position: relative;
        height: 4px;
        background: var(--ext-player-scrubber-track);
        border-radius: var(--radius-pill);
        cursor: pointer;
        outline: none;
      }
      .tplayer-scrubber::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -10px;
        bottom: -10px;
      }
      .tplayer-scrubber:focus-visible { box-shadow: 0 0 0 2px var(--tplayer-active); }
      .tplayer-scrubber:hover .tplayer-scrubber__fill { filter: brightness(1.15); }
      .tplayer-scrubber:hover .tplayer-scrubber__thumb { width: 20px; height: 20px; }
      .tplayer-scrubber__fill {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: var(--ext-player-scrubber-fill);
        border-radius: var(--radius-pill);
        pointer-events: none;
        transition: filter 0.12s;
      }
      .tplayer-scrubber__thumb {
        position: absolute;
        top: 50%;
        width: 14px;
        height: 14px;
        background: var(--ext-player-scrubber-fill);
        border-radius: var(--radius-pill);
        box-shadow: 0 1px 4px color-mix(in srgb, var(--ext-player-scrubber-fill) 50%, transparent);
        transform: translate(-50%, -50%);
        transition: width 0.12s, height 0.12s;
        pointer-events: none;
      }
      .tplayer-scrubber__thumb--active { width: 20px; height: 20px; }
      @media (hover: none) and (pointer: coarse) {
        .tplayer-scrubber__thumb { width: 16px; height: 16px; }
      }

      /* .lps-controls-row / -left / -right */
      .tplayer-controls-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-2) 0 var(--space-3);
        gap: var(--space-2);
      }
      .tplayer-controls-left { display: flex; align-items: center; gap: var(--space-2); flex: 1; min-width: 0; }
      .tplayer-controls-right { display: flex; align-items: center; gap: var(--space-2); flex: 1; justify-content: flex-end; }

      /* .lps-ctrl-btn (+ --play, --cc-active, --settings-active, .lps-volume-btn) */
      .tplayer-ctrl-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: 9px;
        border: none;
        background: transparent;
        color: var(--ext-player-control-icon);
        cursor: pointer;
        transition: background 0.12s, color 0.12s;
        flex-shrink: 0;
        padding: 0;
        touch-action: manipulation;
      }
      .tplayer-ctrl-btn:hover { background: var(--tplayer-btn-hover); color: var(--ext-player-control-icon); }
      .tplayer-ctrl-btn:focus-visible,
      .tplayer-settings-popup button:focus-visible { outline: 2px solid var(--gold-on-dark); outline-offset: 2px; }
      .tplayer-volume-btn { color: var(--ext-player-control-icon-muted); }
      .tplayer-volume-btn:hover { color: var(--ext-player-control-icon); }
      .tplayer-ctrl-btn--play {
        width: 52px;
        height: 52px;
        padding: 0 var(--space-2);
        background: var(--cta);
        color: var(--cta-foreground);
      }
      .tplayer-ctrl-btn--play:hover { background: var(--cta-hover); color: var(--cta-foreground); }
      .tplayer-ctrl-btn--cc-active,
      .tplayer-ctrl-btn--settings-active { color: var(--tplayer-active); background: var(--tplayer-active-bg); }
      .tplayer-ctrl-btn--cc-active:hover,
      .tplayer-ctrl-btn--settings-active:hover { color: var(--tplayer-active); background: var(--tplayer-active-bg-hover); }
      .tplayer-ctrl-divider {
        width: 1px;
        height: 20px;
        background: var(--ext-player-chrome-border);
        margin: 0 var(--space-1);
        flex-shrink: 0;
      }
      /* .lps-ctrl-time — 16px (readability floor), as on the website player. */
      .tplayer-ctrl-time {
        font-size: var(--fs-floor);
        font-weight: 500;
        color: var(--ext-player-control-icon);
        white-space: nowrap;
        letter-spacing: 0.015em;
        padding: 0 var(--space-1);
        font-variant-numeric: tabular-nums;
      }
      .tplayer-volume-slider {
        width: 64px;
        accent-color: var(--gold-on-dark);
        cursor: pointer;
        flex-shrink: 0;
      }
      @container tplayer (max-width: 479px) { .tplayer-volume-slider { display: none; } }
      @container tplayer (max-width: 289px) { .tplayer-t-dur { display: none; } }
      @media (max-width: 767px), (orientation: landscape) and (max-height: 600px), (hover: none) and (pointer: coarse) {
        .tplayer .tplayer-volume-btn,
        .tplayer .tplayer-volume-slider,
        .tplayer .tplayer-ctrl-divider--volume { display: none !important; }
      }
      @container tplayer (max-width: 640px) {
        .tplayer-control-bar { padding: calc(var(--space-6) + var(--space-1)) var(--space-3) 0; }
        .tplayer-scrubber-row { order: 2; margin: 0 calc(-1 * var(--space-3)); padding: var(--space-1) 0 var(--space-2); }
        .tplayer-controls-row { order: 1; margin: 0 calc(-1 * var(--space-1)); padding: var(--space-1) 0 0; }
        .tplayer-ctrl-btn { width: 36px; height: 36px; }
        .tplayer-ctrl-btn--play { width: 40px; height: 40px; }
        .tplayer-ctrl-time { padding: 0; color: var(--ext-player-control-icon-time-mobile); }
      }

      /* .lps-popup-backdrop + .lps-settings-popup (the ⋯ menu), inside the
         player box so it also works in fullscreen. */
      .tplayer-popup-backdrop {
        position: absolute;
        inset: 0;
        z-index: 29;
        background: transparent;
        cursor: default;
      }
      .tplayer-settings-popup {
        position: absolute;
        right: var(--space-3);
        background: var(--ext-surface-2-dark);
        border: 1px solid var(--ext-player-chrome-border);
        border-radius: var(--radius-md);
        padding: var(--space-2);
        min-width: 240px;
        max-width: min(280px, calc(100% - 24px));
        z-index: 30;
        box-shadow: 0 4px 16px color-mix(in srgb, var(--ext-player-bg) 60%, transparent);
        display: flex;
        flex-direction: column;
        gap: calc(var(--space-1) / 2);
        overflow-y: auto;
        overscroll-behavior: contain;
        box-sizing: border-box;
      }
      .tplayer-settings-popup > * { flex-shrink: 0; }
      .tplayer-settings-nav-row,
      .tplayer-settings-option,
      .tplayer-settings-back {
        display: flex;
        align-items: center;
        width: 100%;
        background: transparent;
        border: none;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--fs-floor);
        text-align: left;
        color: var(--ext-player-control-icon);
      }
      .tplayer-settings-nav-row {
        justify-content: space-between;
        gap: var(--space-3);
        padding: var(--space-2);
      }
      .tplayer-settings-nav-row:hover { background: var(--tplayer-menu-hover); }
      .tplayer-settings-nav-row--static { cursor: default; }
      .tplayer-settings-nav-row__label { display: flex; align-items: center; gap: var(--space-2); }
      .tplayer-settings-nav-row__value { font-size: var(--fs-floor); color: var(--text-muted-on-dark); white-space: nowrap; }
      .tplayer-settings-back {
        gap: var(--space-2);
        padding: var(--space-2);
        margin-bottom: calc(var(--space-1) / 2);
        border-bottom: 1px solid var(--tplayer-menu-border-subtle);
        border-radius: 0;
        font-weight: 600;
      }
      .tplayer-settings-back:hover { color: var(--gold-on-dark); }
      .tplayer-settings-option {
        justify-content: space-between;
        gap: var(--space-3);
        padding: var(--space-2);
        color: var(--text-muted-on-dark);
      }
      .tplayer-settings-option:hover { background: var(--tplayer-menu-hover); color: var(--ext-player-control-icon); }
      .tplayer-settings-option span { display: flex; align-items: center; gap: var(--space-2); white-space: normal; line-height: 1.3; }
      .tplayer-settings-option svg[aria-hidden="true"]:last-child { color: var(--gold-on-dark); flex-shrink: 0; }

      /* Buffering scrim — the website's .pm12-player .lps-buffering-overlay
         rule, scoped to this player (the platform original reuses its own
         unscoped globals.css rule, which the website does not have). */
      .tplayer .lps-buffering-overlay {
        position: absolute;
        inset: 0;
        z-index: 6;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        background: rgba(0, 0, 0, 0.22);
      }

      @media (prefers-reduced-motion: reduce) {
        .tplayer-control-bar,
        .tplayer-control-bar[data-autohide="true"],
        .tplayer-caption-overlay { transition: none; }
      }
    `}</style>
  );
}
